import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Loader2, Clock, Calendar, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatMoney } from '@/lib/currency';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { PageSeo } from '@/components/seo/PageSeo';
import { useOrgProfile, useSubmitEnquiry } from '@/hooks/useOrgProfile';
import type { OrgProfileService } from '@/hooks/useOrgProfile';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const bookingSchema = z.object({
  clientName: z.string().trim().min(2, 'Name must be at least 2 characters').max(100, 'Name must be less than 100 characters'),
  clientEmail: z.string().trim().email('Please enter a valid email').max(255, 'Email must be less than 255 characters'),
  address: z.string().trim().min(5, 'Please enter your full address').max(500, 'Address must be less than 500 characters'),
  additionalInfo: z.string().max(1000, 'Additional info must be less than 1000 characters').optional(),
});

type BookingFormData = z.infer<typeof bookingSchema>;

const OrgPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [selectedService, setSelectedService] = useState<OrgProfileService | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const { data: profileData, isLoading, isError } = useOrgProfile(slug);
  const submitEnquiry = useSubmitEnquiry();

  const organization = profileData?.organization ?? null;
  const services = profileData?.services ?? [];
  const businessHours = profileData?.businessHours ?? [];

  const { data: orgSettings } = useOrgSettings(organization?.id);

  const form = useForm<BookingFormData>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      clientName: '',
      clientEmail: '',
      address: '',
      additionalInfo: '',
    },
  });

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minutes} ${ampm}`;
  };

  const handleBookNow = (service: OrgProfileService) => {
    setSelectedService(service);
    setIsSuccess(false);
    form.reset();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
    setIsSuccess(false);
    form.reset();
  };

  const onSubmit = async (data: BookingFormData) => {
    if (!organization || !selectedService) return;

    try {
      await submitEnquiry.mutateAsync({
        organizationId: organization.id,
        serviceId: selectedService.id,
        serviceName: selectedService.name,
        clientName: data.clientName,
        clientEmail: data.clientEmail,
        address: data.address,
        additionalInfo: data.additionalInfo,
      });
      setIsSuccess(true);
      toast.success('Booking request submitted!');
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error('Failed to submit booking request. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !profileData || !organization) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-4xl font-display font-bold text-foreground mb-4">
            Business Not Found
          </h1>
          <p className="text-muted-foreground">
            The business you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  if (profileData.isSuspended) {
    return (
      <main className="min-h-screen bg-background px-6 py-20 text-foreground">
        <div className="mx-auto max-w-xl rounded-lg border border-border bg-card p-8 text-center shadow-sm">
          <h1 className="font-display text-3xl text-foreground">This site is temporarily unavailable</h1>
          <p className="mt-3 text-muted-foreground">Please contact the business owner for more information.</p>
        </div>
      </main>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PageSeo
        pageSlug="services"
        orgIdOverride={organization.id}
        orgName={organization.name}
        fallbackTitle={`${organization.name} Services`}
        fallbackDescription={`View services and request a booking with ${organization.name}.`}
      />
      {/* Header with org name and logo */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            {organization.logo_url ? (
              <img
                src={organization.logo_url}
                alt={`${organization.name} logo`}
                className="h-16 w-16 object-contain rounded-lg"
              />
            ) : (
              <div className="h-16 w-16 rounded-lg bg-primary/10 flex items-center justify-center">
                <span className="text-2xl font-bold text-primary">
                  {organization.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <h1 className="text-2xl md:text-3xl font-display font-bold text-foreground">
                {organization.name}
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Services section */}
      <main className="container mx-auto px-4 py-12">
        <h2 className="text-2xl font-display font-bold text-foreground mb-8">
          Our Services
        </h2>

        {services.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            No services available at the moment. Please check back soon.
          </p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((service) => (
              <Card key={service.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  {service.category && (
                    <p className="text-xs font-medium text-primary uppercase tracking-wider mb-1">
                      {service.category}
                    </p>
                  )}
                  <CardTitle className="text-xl">{service.name}</CardTitle>
                  {service.description && (
                    <CardDescription className="line-clamp-2">
                      {service.description}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-primary">
                      {formatMoney(service.price, orgSettings?.currency)}
                    </span>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      <span>{service.duration}</span>
                    </div>
                  </div>
                  <Button
                    className="w-full"
                    onClick={() => handleBookNow(service)}
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    Book Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Booking Modal */}
      <Dialog open={isModalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {isSuccess ? (
            <div className="text-center py-8 space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <DialogHeader>
                <DialogTitle className="text-center">Booking Request Sent!</DialogTitle>
                <DialogDescription className="text-center">
                  Thank you for your interest. {organization.name} will contact you shortly to confirm your booking.
                </DialogDescription>
              </DialogHeader>
              <Button onClick={handleCloseModal} className="mt-4">
                Close
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>
                  Request Booking
                </DialogTitle>
                <DialogDescription>
                  Fill in your details to request a booking for {selectedService?.name}
                </DialogDescription>
              </DialogHeader>

              {/* Service Summary */}
              {selectedService && (
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{selectedService.name}</span>
                    <span className="font-bold text-primary">{formatMoney(selectedService.price, orgSettings?.currency)}</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>{selectedService.duration}</span>
                  </div>
                </div>
              )}

              {/* Business Hours Accordion */}
              <details className="border rounded-lg">
                <summary className="px-4 py-3 cursor-pointer font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  View Business Hours
                </summary>
                <div className="px-4 pb-3 space-y-1">
                  {businessHours.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No hours set.</p>
                  ) : (
                    DAY_NAMES.map((dayName, index) => {
                      const hours = businessHours.find(h => h.day_of_week === index);
                      return (
                        <div key={index} className="flex justify-between text-sm py-1">
                          <span>{dayName}</span>
                          {hours?.is_open ? (
                            <span>{formatTime(hours.open_time)} - {formatTime(hours.close_time)}</span>
                          ) : (
                            <span className="text-muted-foreground">Closed</span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </details>

              {/* Booking Form */}
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="clientName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Your Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="John Smith" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="clientEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address *</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="john@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Service Address *</FormLabel>
                        <FormControl>
                          <Input placeholder="123 Main St, City, Postcode" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="additionalInfo"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Additional Notes</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Any special requirements or preferred dates/times..."
                            rows={3}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" className="w-full" disabled={submitEnquiry.isPending}>
                    {submitEnquiry.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Submitting...
                      </>
                    ) : (
                      'Request Booking'
                    )}
                  </Button>
                </form>
              </Form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrgPage;
